import React, { useRef, useEffect, useState } from "react";
import { ChevronRight, ChevronDown, Trash2, MoreHorizontal, Check, FileText, CheckSquare, AlertTriangle, Circle, CheckCircle2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { OutlineItem, OutlineItemAction } from "./types";
import styles from "./outliner.module.css";

interface OutlineItemComponentProps {
  item: OutlineItem;
  level: number;
  parentId?: string;
  readonly?: boolean;
  customActions?: OutlineItemAction[];
  onUpdate: (id: string, updates: Partial<OutlineItem>) => void;
  onDelete: (id: string, parentId?: string) => void;
  onKeyDown: (e: React.KeyboardEvent, item: OutlineItem, parentId?: string) => void;
  onDragStart: (e: React.DragEvent, id: string) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent, id: string) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, targetId: string, parentId?: string) => void;
  dragPosition?: "before" | "after" | "inside" | null;
  isSelected?: boolean;
  onToggleSelection?: (itemId: string, isMultiSelect: boolean) => void;
  selectedItems?: string[];
}

const OutlineItemComponent: React.FC<OutlineItemComponentProps> = ({
  item,
  level,
  parentId,
  readonly = false,
  customActions = [],
  onUpdate,
  onDelete,
  onKeyDown,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  dragPosition,
  isSelected = false,
  onToggleSelection,
  selectedItems = [],
}) => {
  const topicRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (topicRef.current && !isEditing) {
      topicRef.current.textContent = item.text;
    }
  }, [item.text, isEditing]);

  const handleTopicFocus = () => {
    setIsEditing(true);
    if (topicRef.current) {
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(topicRef.current);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  };

  const handleTopicBlur = () => {
    setIsEditing(false);
    const newText = topicRef.current?.textContent?.trim() || "";
    if (newText !== item.text) {
      onUpdate(item.id, { text: newText });
    }
  };

  const handleToggleCollapse = () => {
    onUpdate(item.id, { expanded: item.expanded === false ? true : false });
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleSelection) {
      onToggleSelection(item.id, false); // Always multi-select mode
    }
  };

  const handleCompletionToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onUpdate(item.id, { completed: !item.completed });
  };

  // Count total descendants recursively
  const countDescendants = (item: OutlineItem): number => {
    if (!item.children || item.children.length === 0) return 0;
    return item.children.reduce((count, child) => {
      return count + 1 + countDescendants(child);
    }, 0);
  };

  const handleDeleteClick = () => {
    const descendantCount = countDescendants(item);
    if (descendantCount > 0) {
      // Show confirmation dialog
      setShowDeleteConfirm(true);
    } else {
      // Delete directly
      onDelete(item.id, parentId);
    }
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    onDelete(item.id, parentId);
  };

  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = item.expanded !== false;

  const dragClass =
    dragPosition === "before"
      ? styles.dragOverBefore
      : dragPosition === "after"
      ? styles.dragOverAfter
      : dragPosition === "inside"
      ? styles.dragOverInside
      : "";

  // Filter custom actions based on show condition
  const visibleActions = customActions.filter(
    (action) => !action.show || action.show(item)
  );

  return (
    <div
      className={styles.outlineItemContainer}
      draggable={!readonly}
      data-readonly={readonly}
      onDragStart={(e) => onDragStart(e, item.id)}
      onDragEnd={onDragEnd}
    >
      {level > 0 && (
        <div
          className={styles.outlineItemVerticalLine}
          style={{ left: `${level * 24 - 13}px` }}
        />
      )}

      <div
        className={`${styles.outlineItemWrapper} ${dragClass} ${isSelected ? styles.selected : ""}`}
        style={{ marginLeft: `${level * 24}px` }}
        onDragOver={(e) => onDragOver(e, item.id)}
        onDragLeave={onDragLeave}
        onDrop={(e) => onDrop(e, item.id, parentId)}
      >
        {/* Selection Checkbox */}
        {onToggleSelection && (
          <button
            onClick={handleCheckboxClick}
            className={styles.checkbox}
            aria-label="Select item"
            title="Select item for bulk actions"
          >
            <div className={`${styles.checkboxInner} ${isSelected ? styles.checkboxChecked : ""}`}>
              {isSelected && <Check size={10} strokeWidth={2.5} />}
            </div>
          </button>
        )}

        {/* Collapse/Expand Button */}
        <button
          onClick={handleToggleCollapse}
          className={`${styles.collapseBtn} ${!hasChildren ? styles.hidden : ""}`}
          aria-label={isExpanded ? "Collapse" : "Expand"}
        >
          {hasChildren && (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
        </button>

        {/* Completion Checkbox */}
        <button
          onClick={handleCompletionToggle}
          className="flex items-center justify-center w-5 h-5 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
          aria-label={item.completed ? "Mark as incomplete" : "Mark as complete"}
          title={item.completed ? "Mark as incomplete" : "Mark as complete"}
        >
          {item.completed ? (
            <CheckCircle2 size={16} className="text-green-600" strokeWidth={2} />
          ) : (
            <Circle size={16} className="text-gray-400" strokeWidth={2} />
          )}
        </button>

        {/* Topic Input */}
        <div
          ref={topicRef}
          contentEditable={!readonly ? "plaintext-only" : "false"}
          onBlur={handleTopicBlur}
          onFocus={handleTopicFocus}
          onKeyDown={(e) => onKeyDown(e, item, parentId)}
          className={`${styles.topicInput} ${item.completed ? 'line-through opacity-60' : ''}`}
          data-placeholder="Type here..."
          data-item-id={item.id}
          suppressContentEditableWarning
        />

        {/* Status Badges */}
        <div className="flex items-center gap-1 ml-2">
          {item.convertedToPane && (
            <div
              className="flex items-center px-1.5 py-0.5 bg-primary/10 text-primary rounded text-xs"
              title="Converted to pane"
            >
              <FileText size={12} />
            </div>
          )}
          {item.markedAsActionItem && (
            <div
              className="flex items-center px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs"
              title="Marked as action item"
            >
              <CheckSquare size={12} />
            </div>
          )}
        </div>

        {/* Actions */}
        {!readonly && (
          <div className={styles.actionsContainer}>
            {/* Custom Actions */}
            {visibleActions.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className={styles.actionBtn} aria-label="More actions">
                    <MoreHorizontal size={14} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {visibleActions.map((action) => (
                    <DropdownMenuItem
                      key={action.id}
                      onClick={() => action.onClick(item)}
                    >
                      {action.icon && <span className="mr-2">{action.icon}</span>}
                      {action.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Delete Button - always show */}
            <button
              onClick={handleDeleteClick}
              className={`${styles.actionBtn} ${styles.danger}`}
              title="Delete"
              aria-label="Delete item"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div className={styles.childrenContainer}>
          {item.children.filter(Boolean).map((child) => (
            <OutlineItemComponent
              key={child.id}
              item={child}
              level={level + 1}
              parentId={item.id}
              readonly={readonly}
              customActions={customActions}
              onUpdate={onUpdate}
              onDelete={onDelete}
              onKeyDown={onKeyDown}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              dragPosition={dragPosition}
              isSelected={selectedItems.includes(child.id)}
              onToggleSelection={onToggleSelection}
              selectedItems={selectedItems}
            />
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Delete Item with Children?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This item has <strong>{countDescendants(item)} child item{countDescendants(item) !== 1 ? 's' : ''}</strong>.
              Deleting this item will also permanently delete all of its children. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OutlineItemComponent;
